import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DownloadIcon from '@mui/icons-material/GetApp'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'

import IdentityDocumentDisplay from './IdentityDocumentDisplay'
import useIdentityDocumentImage from './useIdentityDocumentImage'

type UserDocumentImageDialogProps = {
  isOpen: boolean
  handleClose: () => void
  userDocumentImageIdList?: string[]
  translationPrefix: string
  title: string
}

const UserDocumentImageDialog = ({
  isOpen,
  handleClose,
  userDocumentImageIdList,
  translationPrefix,
  title,
}: UserDocumentImageDialogProps) => {
  const translate = useTranslate()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const imageIds = userDocumentImageIdList ?? []
  const hasMultipleImages = imageIds.length > 1
  const isPreviousDisabled = currentImageIndex === 0
  const isNextDisabled = currentImageIndex === imageIds.length - 1
  const currentImageId = imageIds[currentImageIndex]
  const { data: currentImageData, isPending: isCurrentImagePending } = useIdentityDocumentImage(currentImageId)
  const currentImageSrc = currentImageData?.data.src

  useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(0)
    }
  }, [isOpen, userDocumentImageIdList])

  const handlePrevious = () => {
    setCurrentImageIndex((index) => Math.max(index - 1, 0))
  }

  const handleNext = () => {
    setCurrentImageIndex((index) => Math.min(index + 1, imageIds.length - 1))
  }

  const handleDownload = () => {
    if (!currentImageId || !currentImageSrc) return

    const link = document.createElement('a')
    link.href = currentImageSrc
    link.download = `document-image-${currentImageId}`
    link.click()
  }

  const downloadLabel = translate(`${translationPrefix}.download`)

  return (
    <Dialog open={isOpen && !isEmpty(imageIds)} onClose={handleClose} maxWidth="md">
      <DialogTitle>{translate(title)}</DialogTitle>
      <DialogContent sx={{ paddingBottom: '12px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, flex: '1 1 auto' }}>
            {currentImageId && <IdentityDocumentDisplay documentImageId={currentImageId} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <IconButton
              aria-label="Previous image"
              disabled={!hasMultipleImages || isPreviousDisabled}
              onClick={handlePrevious}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Tooltip title={downloadLabel}>
              <span>
                <IconButton
                  aria-label={downloadLabel}
                  disabled={!currentImageSrc || isCurrentImagePending}
                  onClick={handleDownload}
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton aria-label="Next image" disabled={!hasMultipleImages || isNextDisabled} onClick={handleNext}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {translate(`${translationPrefix}.close`)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserDocumentImageDialog
